import React, { useCallback, useState } from 'react';
import Translate from '@utils/translation-functions';
import RichText from '@components/rich-text/rich-text';
import {
  FlipcardContainer,
  FlipcardInner,
  FlipcardSide,
  FlipcardBackground,
  FlipcardContent,
  TopSection,
  BottomSection,
  FlipcardBadge,
  FlipcardTitle,
  FlipIcon,
} from './flip-card.styles';
import { FlipCardProps } from './flip-card.types';
import { Link } from 'gatsby';

export const FlipCard: React.FC<FlipCardProps> = ({
  title,
  hasBadge = false,
  badgeBgColour,
  badgeTextColour,
  reverseBadgeBgColour,
  reverseBadgeTextColour,
  bgColour,
  reverseBgColour,
  badgeText,
  content,
  bgImageSrc,
  bgImageAlt,
  reverseBgImageSrc,
  reverseBgImageAlt,
  hasDarkOverlay = false,
  isFlippable = true,
  linkUrl,
  linkText,
  translate = true,
}) => {
  // card should be already flipped if isFlippable is false (should show back side). if not, it should show front side and able to flip
  const [isFlipped, setIsFlipped] = useState(!isFlippable);

  const handleClick = useCallback(() => {
    if (!isFlippable) return;
    setIsFlipped(!isFlipped);
  }, [isFlipped, isFlippable]);

  return (
    <FlipcardContainer isFlippable={isFlippable} onClick={handleClick}>
      <FlipcardInner isFlipped={isFlipped}>
        {isFlippable && (
          <FlipcardSide
            backgroundColor={bgColour}
            isBack={false}
            data-testid="flipcard-front-side"
          >
            <FlipcardBackground
              backgroundImage={bgImageSrc}
              darkOverlay={hasDarkOverlay}
              data-testid="flipcard-background"
            />
            <FlipcardContent>
              <TopSection>
                {hasBadge && (
                  <FlipcardBadge
                    backgroundColor={badgeBgColour}
                    color={badgeTextColour}
                    data-testid="flipcard-front-badge"
                  >
                    {badgeText}
                  </FlipcardBadge>
                )}
                <FlipcardTitle>{title}</FlipcardTitle>
              </TopSection>
              <BottomSection>
                <FlipIcon>
                  <img
                    src="https://uploads-ssl.webflow.com/665871d25ce3f664ba8a7e07/66a8ad049347e24fc512737c_Group%20574.svg"
                    alt="Flip"
                  />
                  <span>{Translate('Flip', translate)}</span>
                </FlipIcon>
              </BottomSection>
            </FlipcardContent>
          </FlipcardSide>
        )}
        <FlipcardSide
          backgroundColor={reverseBgColour}
          isBack={true}
          data-testid="flipcard-back-side"
        >
          <FlipcardBackground
            backgroundImage={reverseBgImageSrc}
            darkOverlay={hasDarkOverlay}
          />
          <FlipcardContent>
            <TopSection>
              {hasBadge && (
                <FlipcardBadge
                  // fallback to inverted colours if reverse badge colours are not provided
                  backgroundColor={reverseBadgeBgColour || badgeTextColour}
                  color={reverseBadgeTextColour || badgeBgColour}
                >
                  {badgeText}
                </FlipcardBadge>
              )}
              <RichText content={content} />
            </TopSection>
            {linkUrl && linkText && (
              <BottomSection>
                <Link to={linkUrl}>{linkText}</Link>
              </BottomSection>
            )}
          </FlipcardContent>
        </FlipcardSide>
      </FlipcardInner>
    </FlipcardContainer>
  );
};
